import { Copy, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/settings/settings-controls';
import { TimeField } from '@/components/pages/work-plan/planner-bits';
import {
  MAX_WINDOWS_PER_DAY,
  WEEKDAY_KEYS,
  WEEKDAY_LABELS,
  canonicalHHMM,
  copyDayToDays,
  minutesFromHHMM,
} from '@/lib/bot-service-hours';

// Presentational 7-row weekly-hours editor: one row per day with an Open/Closed switch
// (closed = an empty window list), 1–2 {start,end} windows, and per-day error lines keyed
// `${errorPrefix}.${day}` from the shared validation. Weekday rows get an "Apply to Mon–Fri"
// shortcut. Purely controlled — every change hands the parent a complete new weekly map.

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const DEFAULT_WINDOW = { start: '08:30', end: '17:30' };

// Seed for a second window: start where the last one ends (touching is valid) and run four
// hours, capped at end of day — so "+ Add hours" never lands in an invalid state.
function followOnWindow(windows) {
  const last = windows[windows.length - 1];
  const lastEnd = minutesFromHHMM(last?.end);
  if (lastEnd < 0 || lastEnd >= 23 * 60 + 59) return { start: '14:00', end: '17:30' };
  const end = Math.min(lastEnd + 4 * 60, 23 * 60 + 59);
  const toHHMM = (total) =>
    `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  return { start: canonicalHHMM(last.end), end: toHHMM(end) };
}

export function WeeklyHoursGrid({ weekly, errors = {}, errorPrefix = 'weekly', disabled = false, onChange }) {
  const windowsFor = (key) => (Array.isArray(weekly?.[key]) ? weekly[key] : []);
  const setDay = (key, windows) => onChange({ ...weekly, [key]: windows });
  const setWindow = (key, index, patch) =>
    setDay(key, windowsFor(key).map((window, i) => (i === index ? { ...window, ...patch } : window)));

  return (
    <div className="flex flex-col gap-1.5">
      {WEEKDAY_KEYS.map((key) => {
        const label = WEEKDAY_LABELS[key];
        const windows = windowsFor(key);
        const open = windows.length > 0;
        const error = errors[`${errorPrefix}.${key}`];
        return (
          <div key={key} className="rounded-lg border border-border bg-card px-3 py-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="w-9 shrink-0 text-[11px] font-bold uppercase tracking-wide text-foreground">
                {label}
              </span>
              <Switch
                checked={open}
                disabled={disabled}
                label={`${label} open`}
                onChange={(next) => setDay(key, next ? [{ ...DEFAULT_WINDOW }] : [])}
              />
              {open ? (
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1.5">
                  {windows.map((window, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                      <div className="w-[6.5rem]">
                        <TimeField
                          value={window.start}
                          disabled={disabled}
                          aria-label={`${label} window ${index + 1} start`}
                          onChange={(event) => setWindow(key, index, { start: event.target.value })}
                          className={cn(error && 'border-destructive')}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">–</span>
                      <div className="w-[6.5rem]">
                        <TimeField
                          value={window.end}
                          disabled={disabled}
                          aria-label={`${label} window ${index + 1} end`}
                          onChange={(event) => setWindow(key, index, { end: event.target.value })}
                          className={cn(error && 'border-destructive')}
                        />
                      </div>
                      {!disabled ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          aria-label={`Remove ${label} window ${index + 1}`}
                          onClick={() => setDay(key, windows.filter((_, i) => i !== index))}
                        >
                          <Trash2 className="size-3.5 text-muted-foreground" aria-hidden />
                        </Button>
                      ) : null}
                    </div>
                  ))}
                  {!disabled && windows.length < MAX_WINDOWS_PER_DAY ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-muted-foreground"
                      onClick={() => setDay(key, [...windows, followOnWindow(windows)])}
                    >
                      <Plus className="mr-1 size-3.5" aria-hidden />
                      Add hours
                    </Button>
                  ) : null}
                </div>
              ) : (
                <span className="flex-1 text-xs text-muted-foreground">Closed</span>
              )}
              {!disabled && open && WEEKDAYS.includes(key) ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-[11px] text-muted-foreground"
                  title={`Copy ${label}'s hours to every weekday`}
                  onClick={() => onChange(copyDayToDays(weekly, key, WEEKDAYS))}
                >
                  <Copy className="mr-1 size-3" aria-hidden />
                  Apply to Mon–Fri
                </Button>
              ) : null}
            </div>
            {error ? <p className="mt-1 text-[11px] text-destructive">{error}</p> : null}
          </div>
        );
      })}
    </div>
  );
}
